import { DEFAULT_UNIFORM_BIN_COUNT, KPI_RANGE_GAP, MAX_CUSTOM_BREAKPOINTS, MIN_KPI_BIN_COUNT } from 'constants/ppa';

export type BinningMethod = 'uniform' | 'custom';

export type KpiAxisConfiguration = {
    min: number;
    max: number;
    binningMethod: BinningMethod;
    binCount: number;
    breakpoints: number[];
};

export type KpiBin = {
    start: number;
    end: number;
    label: string;
};

export type KpiDataRange = {
    defaultMin: number;
    defaultMax: number;
};

export type KpiPreviewResult = {
    bins: KpiBin[];
    binCount: number;
    binSize: number;
    breakpoints: number[];
};

/** PA-01 unit representation for KPI bin preview labels (e.g. $22-$26, 22oz-26oz). */
export type KpiBinLabelAffixes = {
    prefix?: string;
    suffix?: string;
};

/** Maps PA meta `unit_rep` to bin label affixes; tenant currency is a prefix, others are suffixes. */
export const toKpiBinLabelAffixesFromUnitRep = (
    unitRep: string,
    currencyPrefix: string,
): KpiBinLabelAffixes => {
    if (unitRep === currencyPrefix) {
        return { prefix: currencyPrefix };
    }
    return { suffix: unitRep };
};

export {
    DEFAULT_KPI_DATA_RANGE,
    DEFAULT_UNIFORM_BIN_COUNT,
    KPI_RANGE_GAP,
    MAX_CUSTOM_BREAKPOINTS,
    MIN_KPI_BIN_COUNT,
} from 'constants/ppa';

export const createDefaultKpiAxisConfiguration = (
    dataRange: KpiDataRange,
    metaDefaults?: Partial<Pick<KpiAxisConfiguration, 'binningMethod' | 'binCount'>>,
): KpiAxisConfiguration => ({
    min: dataRange.defaultMin,
    max: dataRange.defaultMax,
    binningMethod: metaDefaults?.binningMethod ?? 'uniform',
    binCount: metaDefaults?.binCount ?? DEFAULT_UNIFORM_BIN_COUNT,
    breakpoints: [],
});

export const getKpiRangeSpan = (min: number, max: number) => Math.max(0, max - min);

export const getUniformBinSize = (min: number, max: number, binCount: number) => {
    const span = getKpiRangeSpan(min, max);
    if (binCount <= 0 || span <= 0) {
        return 0;
    }
    return Math.round(span / binCount);
};

export const getUniformBinCountFromSize = (min: number, max: number, binSize: number) => {
    const span = getKpiRangeSpan(min, max);
    if (binSize <= 0 || span <= 0) {
        return 1;
    }
    return Math.max(MIN_KPI_BIN_COUNT, Math.min(span, Math.round(span / binSize)));
};

export const formatBinLabel = (
    start: number,
    end: number,
    affixes: KpiBinLabelAffixes = {},
): string => {
    const prefix = affixes.prefix ?? '';
    const suffix = affixes.suffix ?? '';
    return `${prefix}${start}${suffix}-${prefix}${end}${suffix}`;
};

export const buildUniformBins = (
    min: number,
    max: number,
    binCount: number,
    affixes: KpiBinLabelAffixes = {},
): KpiBin[] => {
    if (binCount <= 0 || max <= min) {
        return [];
    }

    const binSize = getUniformBinSize(min, max, binCount);
    const bins: KpiBin[] = [];

    for (let index = 0; index < binCount; index += 1) {
        const start = min + index * binSize;
        const end = index === binCount - 1 ? max : min + (index + 1) * binSize;
        bins.push({
            start,
            end,
            label: formatBinLabel(start, end, affixes),
        });
    }

    return bins;
};

export const buildCustomBins = (
    min: number,
    max: number,
    breakpoints: number[],
    affixes: KpiBinLabelAffixes = {},
): KpiBin[] => {
    const sortedBreakpoints = [...breakpoints].sort((left, right) => left - right);
    const boundaries = [min, ...sortedBreakpoints, max];
    const bins: KpiBin[] = [];

    for (let index = 0; index < boundaries.length - 1; index += 1) {
        const start = boundaries[index];
        const end = boundaries[index + 1];
        if (start === end) continue;
        bins.push({
            start,
            end,
            label: formatBinLabel(start, end, affixes),
        });
    }

    return bins;
};

export const buildKpiPreview = (
    config: KpiAxisConfiguration,
    affixes: KpiBinLabelAffixes = {},
): KpiPreviewResult => {
    const bins =
        config.binningMethod === 'uniform'
            ? buildUniformBins(config.min, config.max, config.binCount, affixes)
            : buildCustomBins(config.min, config.max, config.breakpoints, affixes);

    const binSize =
        config.binningMethod === 'uniform'
            ? getUniformBinSize(config.min, config.max, config.binCount)
            : 0;

    const breakpoints =
        config.binningMethod === 'uniform'
            ? buildUniformBreakpointTicks(config.min, config.max, config.binCount)
            : [...config.breakpoints].sort((left, right) => left - right);

    return {
        bins,
        binCount: bins.length,
        binSize,
        breakpoints,
    };
};

export const buildUniformBreakpointTicks = (min: number, max: number, binCount: number) => {
    const binSize = getUniformBinSize(min, max, binCount);
    const ticks: number[] = [];

    for (let index = 1; index < binCount; index += 1) {
        ticks.push(min + index * binSize);
    }

    return ticks;
};

export const clampKpiRange = (
    min: number,
    max: number,
    dataRange: KpiDataRange,
): { min: number; max: number } => {
    const clampedMin = Math.max(dataRange.defaultMin, Math.min(min, dataRange.defaultMax - KPI_RANGE_GAP));
    const clampedMax = Math.min(dataRange.defaultMax, Math.max(max, dataRange.defaultMin + KPI_RANGE_GAP));

    if (clampedMin >= clampedMax) {
        return {
            min: Math.max(dataRange.defaultMin, clampedMax - KPI_RANGE_GAP),
            max: clampedMax,
        };
    }

    return { min: clampedMin, max: clampedMax };
};

export const kpiConfigsAreEqual = (left: KpiAxisConfiguration, right: KpiAxisConfiguration) =>
    left.min === right.min &&
    left.max === right.max &&
    left.binningMethod === right.binningMethod &&
    left.binCount === right.binCount &&
    left.breakpoints.join(',') === right.breakpoints.join(',');

export const sanitizeKpiBreakpoints = (
    breakpoints: number[],
    min: number,
    max: number,
    dataRange: KpiDataRange,
) =>
    [...new Set(breakpoints)]
        .filter(
            (value) =>
                Number.isInteger(value) &&
                value > min &&
                value < max &&
                value >= dataRange.defaultMin &&
                value <= dataRange.defaultMax,
        )
        .sort((left, right) => left - right)
        .slice(0, MAX_CUSTOM_BREAKPOINTS);

export const clampBreakpointValue = (
    value: number,
    index: number,
    min: number,
    max: number,
    sortedBreakpoints: number[],
): number => {
    const lower = index === 0 ? min + KPI_RANGE_GAP : sortedBreakpoints[index - 1] + KPI_RANGE_GAP;
    const upper =
        index === sortedBreakpoints.length - 1
            ? max - KPI_RANGE_GAP
            : sortedBreakpoints[index + 1] - KPI_RANGE_GAP;

    if (lower > upper) {
        return sortedBreakpoints[index];
    }

    return Math.max(lower, Math.min(upper, Math.round(value)));
};

export const getDefaultNewBreakpoint = (
    min: number,
    max: number,
    breakpoints: number[],
): number | null => {
    if (max - min <= KPI_RANGE_GAP) {
        return null;
    }

    const sorted = [...breakpoints].sort((left, right) => left - right);
    const boundaries = [min, ...sorted, max];
    let largestGap = 0;
    let candidate = min + KPI_RANGE_GAP;

    for (let index = 0; index < boundaries.length - 1; index += 1) {
        const start = boundaries[index];
        const end = boundaries[index + 1];
        const gap = end - start;

        if (gap > largestGap) {
            largestGap = gap;
            const midpoint = Math.round((start + end) / 2);
            candidate =
                midpoint <= start
                    ? start + KPI_RANGE_GAP
                    : midpoint >= end
                      ? end - KPI_RANGE_GAP
                      : midpoint;
        }
    }

    if (candidate <= min || candidate >= max) {
        return null;
    }

    return candidate;
};

export const isBreakpointInRange = (value: number, min: number, max: number) =>
    Number.isInteger(value) && value > min && value < max;
